import React, { useState } from "react";
import { api } from "../lib/api";
import { useData } from "../hooks/useData";
import { toast } from "sonner";
import { Card, Button, Select } from "./shared";
import { Database, Download, Upload, RefreshCw, HardDriveDownload } from "lucide-react";

const fmtSize = (b) => (!b ? "0 KB" : b > 1048576 ? (b / 1048576).toFixed(1) + " MB" : (b / 1024).toFixed(0) + " KB");
const fmtDT = (s) => (s ? new Date(s).toLocaleString() : "—");

export const BackupRestore = () => {
  const { data: status, reload } = useData("/backup/status");
  const [busy, setBusy] = useState("");
  const [file, setFile] = useState(null);

  const create = async () => {
    setBusy("create");
    try { const { data } = await api.post("/backup/create"); toast.success(`Backup created — ${data.docs} records`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Backup failed. The previous successful backup has been preserved."); }
    finally { setBusy(""); }
  };
  const download = async () => {
    try {
      const res = await api.get("/backup/download", { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = u; a.download = "ASV_Apparels_Backup.json"; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(e.response?.data?.detail || "No backup to download"); }
  };
  const restoreLatest = async () => {
    if (!window.confirm("Restoring this backup will REPLACE the current database with the latest stored backup. A safety backup of the current data will be taken first. Please make sure you have a recent backup before continuing. Continue?")) return;
    setBusy("restore");
    try { const { data } = await api.post("/backup/restore", {}); toast.success(`Restored ${data.restored_docs} records`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
    finally { setBusy(""); }
  };
  const restoreFile = async () => {
    if (!file) { toast.error("Choose a backup .json file first"); return; }
    if (!window.confirm("Restoring this file will REPLACE the current database. A safety backup of the current data will be taken first. Continue?")) return;
    setBusy("restorefile");
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/backup/restore-file", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Restored ${data.restored_docs} records`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
    finally { setBusy(""); }
  };
  const setCfg = async (patch) => {
    try { await api.post("/backup/config", patch); reload(); } catch { toast.error("Failed to save setting"); }
  };

  const s = status || {};
  const last = s.last_backup;
  return (
    <Card className="p-5 mb-6" data-testid="backup-card">
      <div className="flex items-center gap-2 mb-1"><Database size={16} className="text-blue-800" /><p className="text-sm font-semibold text-slate-800 font-head">Backup & Restore</p></div>
      <p className="text-xs text-slate-400 mb-4">Complete database backup, stored durably so it survives every republish/redeploy. The previous backup is never removed until a new one is verified. Google Drive backup coming soon.</p>
      <div className="grid sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-slate-50 rounded-md p-3"><p className="text-xs text-slate-500">Last Successful Backup</p><p className="text-sm font-medium" data-testid="backup-last">{fmtDT(last?.created_at)}</p></div>
        <div className="bg-slate-50 rounded-md p-3"><p className="text-xs text-slate-500">Backup Size</p><p className="text-sm font-medium">{fmtSize(last?.size_bytes)}</p></div>
        <div className="bg-slate-50 rounded-md p-3"><p className="text-xs text-slate-500">Backup Status</p><p className={`text-sm font-medium ${s.available ? "text-emerald-700" : "text-slate-500"}`}>{s.available ? "Successful" : "No backup yet"}</p></div>
        <div className="bg-slate-50 rounded-md p-3"><p className="text-xs text-slate-500">Backup Available</p><p className={`text-sm font-medium ${s.available ? "text-emerald-700" : "text-slate-500"}`}>{s.available ? "Yes" : "No"}</p></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div><label className="block text-xs text-slate-500 mb-1">Backup Location</label>
          <Select value={s.location || "download"} onChange={(e) => setCfg({ location: e.target.value })} data-testid="backup-location"
            options={[{ value: "download", label: "Download file (to your computer)" }, { value: "gdrive", label: "Google Drive (coming soon)" }]} /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Automatic Backup on Logout</label>
          <Select value={s.auto_logout_backup ? "1" : "0"} onChange={(e) => setCfg({ auto_logout_backup: e.target.value === "1" })} data-testid="backup-auto"
            options={[{ value: "0", label: "OFF" }, { value: "1", label: "ON — keep one rolling latest backup" }]} /></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={create} disabled={busy === "create"} data-testid="backup-create-btn"><RefreshCw size={15} /> {busy === "create" ? "Creating…" : "Create Backup Now"}</Button>
        <Button variant="secondary" onClick={download} data-testid="backup-download-btn"><Download size={15} /> Download Latest</Button>
        <Button variant="secondary" onClick={restoreLatest} disabled={busy === "restore"} data-testid="backup-restore-btn"><HardDriveDownload size={15} /> Restore Latest</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3 border-t border-slate-100 pt-3">
        <span className="text-xs text-slate-500">Restore from a backup file:</span>
        <input type="file" accept=".json" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="backup-restore-file-input" />
        <Button variant="secondary" onClick={restoreFile} disabled={busy === "restorefile"} data-testid="backup-restore-file-btn"><Upload size={15} /> Restore from File</Button>
      </div>
    </Card>
  );
};
